// frontend/src/hooks/useOrderWsStatus.js
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useWebSocket } from "../hooks/useWebSocket";

/**
 * Push-first status using the app WebSocket.
 * - Immediately primes from REST once for fast paint.
 * - On mount / connector change, asks server to push the latest snapshot.
 * - Listens for { type: "order_ws_status", connector_id, status } frames.
 */
export function useOrderWsStatusWS(connectorId) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
    const [status, setStatus] = useState(null);

    // Handle inbound messages (works with provider or raw socket)
    const handleMessage = useCallback(
        (raw) => {
            try {
                const msg = typeof raw === "string" ? JSON.parse(raw) : raw;
                if (msg?.type === "order_ws_status" && msg.connector_id === connectorId) {
                    setStatus(msg.status || null);
                }
            } catch {
                /* ignore parse errors */
            }
        },
        [connectorId]
    );

    // Use the current hook signature: useWebSocket({ token, onMessage })
    const token = localStorage.getItem("token");
    const { socket, status: wsState } = useWebSocket({ token, onMessage: handleMessage });

    // Prime via REST once per connector (so the chip shows something even if no push yet)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(
                    `${API_BASE_URL}/order-ws/status/${connectorId}`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
                );
                if (!cancelled) setStatus(data);
            } catch {
                /* ignore; WS push will update later */
            }
        })();
        return () => { cancelled = true; };
    }, [API_BASE_URL, connectorId]);

    // Request a hot-sync snapshot from the server (works after reconnect too)
    const requestSync = useCallback(() => {
        try {
            if (!socket || socket.readyState !== 1) return;
            socket.send(JSON.stringify({ type: "order_ws_status_sync", connector_id: connectorId }));
        } catch { /* ignore */ }
    }, [socket, connectorId]);

    // Ask for a sync whenever we have a socket and when connector changes
    useEffect(() => { requestSync(); }, [requestSync, wsState, connectorId]);

    // Fallback: also attach to the raw socket in case provider did not plumb onMessage
    useEffect(() => {
        if (!socket) return;
        const onMsg = (evt) => handleMessage(evt?.data ?? evt);
        if (typeof socket.addEventListener === "function") {
            socket.addEventListener("message", onMsg);
            return () => socket.removeEventListener("message", onMsg);
        } else if ("onmessage" in socket) {
            const prev = socket.onmessage;
            socket.onmessage = (evt) => {
                onMsg(evt);
                if (typeof prev === "function") prev(evt);
            };
            return () => { socket.onmessage = prev || null; };
        }
        return () => {};
    }, [socket, handleMessage]);

    // Manual reconnect action (re-use your existing REST API)
    const reconnect = useCallback(async () => {
        await axios.post(
            `${API_BASE_URL}/order-ws/reconnect/${connectorId}`,
            {},
            { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );
        requestSync();
    }, [API_BASE_URL, connectorId, requestSync]);

    return { status, reconnect };
}

/**
 * Optional polling-based hook (kept for screens where polling is acceptable).
 */
export function useOrderWsStatus(connectorId, { intervalMs = 5000 } = {}) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const timerRef = useRef(null);
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

    const fetchStatus = useCallback(async () => {
        try {
            const token = localStorage.getItem("token");
            const { data } = await axios.get(
                `${API_BASE_URL}/order-ws/status/${connectorId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setStatus(data);
        } catch (e) {
            setStatus({
                healthy: false,
                connected: false,
                is_running: false,
                last_error: e?.message,
            });
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, connectorId]);

    const reconnect = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            await axios.post(
                `${API_BASE_URL}/order-ws/reconnect/${connectorId}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            await fetchStatus();
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, connectorId, fetchStatus]);

    useEffect(() => {
        fetchStatus();
        timerRef.current = setInterval(fetchStatus, intervalMs);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [fetchStatus, intervalMs]);

    return { status, loading, reconnect, refresh: fetchStatus };
}
